<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, nextTick, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import Dialog from "primevue/dialog"
import Button from "primevue/button"
import type { Building } from "@types"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useAuthStore } from "@core/stores/auth.store"
import type { BuildingModifyAddress } from "@core/components/building/form/BuildingUpdateAddressForm.vue"
import {
    BUILDING_INFO_GENERAL_SECTION_IDS,
    useBuildingInfoGeneralSectionVisibility,
} from "@core/composables/useBuildingInfoGeneralSections"

// ================================
// ROUTE & STORES
// ================================
const route = useRoute()
const router = useRouter()
const buildingStore = useBuildingStore()
const authStore = useAuthStore()
const { building, isFetchingBuilding, getBuildingIsViewOnly } =
    storeToRefs(buildingStore)
const { isEmployeWriteOrHigher, hasOfferEnergyManager } = storeToRefs(authStore)

const { t } = useI18n({
    messages: {
        fr: {
            pageTitle: "Informations générales",
            modifyAddress: "Modifier l’adresse",
            returnToInfo: "Retour aux informations",
            confirmTitle: "Modifier l’adresse du bâtiment ?",
            warn1: "La modification de l’adresse peut affecter la parcelle cadastrale et les liens entre bâtiments.",
            warn2: "Les liens existants seront supprimés ; vous pourrez les recréer depuis cette page.",
            confirm: "Continuer",
            cancelDialog: "Annuler",
        },
    },
})

const getBuildingId = computed(
    () => (route.params._id_building as string | undefined) ?? ""
)

const view = ref<"info" | "change-address">("info")
const confirmVisible = ref(false)

function syncViewFromQuery(): void {
    view.value = route.query.changeAddress ? "change-address" : "info"
}

watch(
    () => route.query,
    () => syncViewFromQuery(),
    { deep: true, immediate: true }
)

/** Consultation seule (droits) : champs en `readonly`. */
const getReadonly = computed(
    () => !isEmployeWriteOrHigher.value || Boolean(getBuildingIsViewOnly.value)
)

/** Bâtiment ouvert en lecture seule : champs en `disabled` (pas `readonly`). */
const getDisabled = computed(() => Boolean(getBuildingIsViewOnly.value))

const getCanEditToolbar = computed(
    () => isEmployeWriteOrHigher.value && !getBuildingIsViewOnly.value
)

const sectionIds = BUILDING_INFO_GENERAL_SECTION_IDS

const { getIsHousingCardDisplay, getShowRelation, getShowBuildingBlock } =
    useBuildingInfoGeneralSectionVisibility()

const knownInfoGeneralSectionIds = new Set(
    Object.values(BUILDING_INFO_GENERAL_SECTION_IDS)
)

/** Évite de redéfiler vers l’ancre à chaque refresh du bâtiment (ex. sauvegarde) alors que le hash est inchangé. */
const scrollInfoGeneralPrevHash = ref(route.hash)
const scrollInfoGeneralPrevView = ref(view.value)
const scrollInfoGeneralHadBuilding = ref(false)

/**
 * Défilement fluide vers la section correspondant au hash d’URL (sous-onglets sidebar).
 */
function scrollToInfoGeneralSectionFromHash(): void {
    const raw = route.hash
    if (!raw) return
    const id = raw.startsWith("#") ? raw.slice(1) : raw
    const sectionId =
        id as (typeof BUILDING_INFO_GENERAL_SECTION_IDS)[keyof typeof BUILDING_INFO_GENERAL_SECTION_IDS]
    if (!knownInfoGeneralSectionIds.has(sectionId)) return
    void nextTick(() => {
        requestAnimationFrame(() => {
            document.getElementById(sectionId)?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            })
        })
    })
}

watch(
    () => ({
        hash: route.hash,
        view: view.value,
        hasBuilding: Boolean(building.value),
    }),
    ({ hash, view: currentView, hasBuilding }) => {
        if (!hasBuilding) {
            scrollInfoGeneralHadBuilding.value = false
            return
        }
        if (currentView !== "info" || !hash) {
            scrollInfoGeneralPrevHash.value = hash
            scrollInfoGeneralPrevView.value = currentView
            scrollInfoGeneralHadBuilding.value = hasBuilding
            return
        }
        const hashChanged = hash !== scrollInfoGeneralPrevHash.value
        const viewBecameInfo =
            currentView === "info" && scrollInfoGeneralPrevView.value !== "info"
        const buildingJustLoaded =
            hasBuilding && !scrollInfoGeneralHadBuilding.value

        scrollInfoGeneralPrevHash.value = hash
        scrollInfoGeneralPrevView.value = currentView
        scrollInfoGeneralHadBuilding.value = hasBuilding

        if (hashChanged || viewBecameInfo || buildingJustLoaded) {
            scrollToInfoGeneralSectionFromHash()
        }
    },
    { flush: "post", immediate: true }
)

function getMongoId(b: Building | null): string {
    return b?._id ?? b?._id_building ?? ""
}

const getAddressFormBuilding = computed((): BuildingModifyAddress | null => {
    const b = building.value
    if (!b) return null
    const ext = b as Building & {
        geometry?: BuildingModifyAddress["geometry"]
    }
    return {
        _id: getMongoId(b),
        _id_building: b._id_building,
        address: b.address,
        level: b.infos?.niveau,
        infos: b.infos,
        geometry:
            ext.geometry ??
            ({
                address: {
                    coordinates: b.location?.coordinates as [number, number],
                },
                cadastre: b.geometry?.cadastre,
            } as BuildingModifyAddress["geometry"]),
        location: b.location,
    }
})

function onClickToolbarAddress(): void {
    if (view.value === "change-address") {
        onReturnToInfo()
    } else {
        confirmVisible.value = true
    }
}

function onConfirmChangeAddress(): void {
    confirmVisible.value = false
    void router.replace({
        path: route.path,
        query: { ...route.query, changeAddress: "true" },
    })
}

function onReturnToInfo(): void {
    const next = { ...route.query }
    delete next.changeAddress
    void router.replace({ path: route.path, query: next })
}

async function onAddressFormFinish(): Promise<void> {
    const id = getBuildingId.value
    if (id) await buildingStore.fetchBuilding(id, { silent: true })
    onReturnToInfo()
}

function onAddressFormCancel(): void {
    onReturnToInfo()
}

function onRequestChangeAddress(): void {
    confirmVisible.value = true
}
</script>

<template>
    <div class="page page-building-info-general flex flex-col gap-6 pb-12">
        <HeaderPageBuilding
            :title="t('pageTitle')"
            class-header="flex justify-between items-center flex-wrap gap-4"
        >
            <BaseButton
                v-if="getCanEditToolbar"
                severity="secondary"
                :text="view === 'change-address'"
                :label="
                    view === 'change-address'
                        ? t('returnToInfo')
                        : t('modifyAddress')
                "
                :icon="
                    view === 'change-address'
                        ? 'pi pi-arrow-left'
                        : 'pi pi-pencil'
                "
                @click="onClickToolbarAddress"
            />
        </HeaderPageBuilding>

        <BaseMessage
            v-if="buildingStore.hasErrorBuilding"
            severity="error"
            :closable="false"
            class="mx-auto w-full"
        >
            {{ buildingStore.errorBuilding?.message }}
        </BaseMessage>

        <div v-else-if="isFetchingBuilding && !building" class="mx-auto w-full">
            <BaseSkeleton width="100%" height="24rem" />
        </div>

        <template v-else-if="building">
            <BuildingUpdateAddressForm
                v-if="view === 'change-address' && getAddressFormBuilding"
                class="w-full mx-auto px-2"
                :building="getAddressFormBuilding"
                @update:finish="onAddressFormFinish"
                @click:cancel="onAddressFormCancel"
            />

            <div v-else class="flex flex-col gap-10 w-full">
                <div :id="sectionIds.address" class="scroll-mt-6">
                    <BuildingInfoGeneralAddress
                        :readonly="getReadonly"
                        :disabled="getDisabled"
                        :on-request-change-address="onRequestChangeAddress"
                    />
                </div>
                <div
                    v-if="getShowRelation"
                    :id="sectionIds.relation"
                    class="scroll-mt-6"
                >
                    <BuildingInfoGeneralRelation
                        :readonly="getReadonly"
                        :disabled="getDisabled"
                    />
                </div>
                <div
                    v-if="getShowBuildingBlock"
                    :id="sectionIds.building"
                    class="scroll-mt-6"
                >
                    <BuildingInfoGeneralBuilding
                        :readonly="getReadonly"
                        :disabled="getDisabled"
                    />
                </div>
                <div
                    v-if="getIsHousingCardDisplay"
                    :id="sectionIds.house"
                    class="scroll-mt-6"
                >
                    <BuildingInfoGeneralHouse
                        :readonly="getReadonly"
                        :disabled="getDisabled"
                    />
                </div>
                <div
                    v-if="hasOfferEnergyManager"
                    :id="sectionIds.usageProfile"
                    class="scroll-mt-6"
                >
                    <UsageProfileCard
                        class="w-full"
                        :readonly="getReadonly"
                        :disabled="getDisabled"
                    />
                </div>
                <div :id="sectionIds.actors" class="scroll-mt-6">
                    <BuildingInfoGeneralActors
                        :readonly="getReadonly"
                        :disabled="getDisabled"
                    />
                </div>
            </div>
        </template>

        <Dialog
            v-model:visible="confirmVisible"
            modal
            :header="t('confirmTitle')"
            class="w-full max-w-md"
        >
            <div
                class="flex flex-col gap-3 text-surface-800 text-sm leading-relaxed"
            >
                <p>{{ t("warn1") }}</p>
                <p>{{ t("warn2") }}</p>
            </div>
            <template #footer>
                <div class="flex justify-end gap-2 flex-wrap">
                    <Button
                        :label="t('cancelDialog')"
                        severity="secondary"
                        @click="confirmVisible = false"
                    />
                    <Button
                        :label="t('confirm')"
                        @click="onConfirmChangeAddress"
                    />
                </div>
            </template>
        </Dialog>
    </div>
</template>
